/**
 * Hasta cuándo se puede deshacer una corrida.
 *
 * El plazo lo fija la retención del snapshot, que sale de configuración. Una vez vencido
 * ya no guardamos los precios anteriores y no hay forma de volver atrás, así que la fecha
 * se muestra siempre: en el historial y en el detalle.
 */

import type { Corrida } from './api';
import { fecha } from './formato';

export type EstadoDeshacer = {
  readonly clase: 'disponible' | 'vencido' | 'no_aplica';
  /** Suelto, para una etiqueta o una celda: "Hasta el 16/09/2026". */
  readonly texto: string;
  /**
   * El mismo dato como parte de una oración, para el renglón corrido de la tarjeta del
   * historial: "Manual · 232 actualizadas · se puede deshacer hasta el 16/09/2026".
   *
   * `null` cuando decirlo no agrega nada: en una corrida de deshacer, que no se pueda
   * deshacer es la definición, y el renglón se ahorra cuatro palabras.
   */
  readonly frase: string | null;
};

/** Mientras esté en alguno de estos, la corrida se sigue moviendo. */
const EN_VUELO: ReadonlySet<Corrida['estado']> = new Set(['preview', 'queued', 'running']);

/**
 * Espejo de `puedeDeshacer` del backend (`routes/corridas.ts`), y por el mismo motivo:
 * **el criterio es si escribió algo, no en qué estado terminó.** Una corrida cancelada
 * después de tocar 2.000 precios, o una que falló a la mitad, dejaron precios cambiados
 * en la tienda y tienen que poder revertirse.
 *
 * Si los dos se desincronizan, el botón aparece y el backend contesta 409, o —peor— no
 * aparece y el comerciante no tiene salida. El backend manda; esto es sólo la pantalla.
 */
export function estadoDeshacer(corrida: Corrida, ahora: Date = new Date()): EstadoDeshacer {
  /*
    Ninguno de estos casos dice "—".

    El guión aparecía en tres situaciones que significan cosas distintas —es un deshacer,
    todavía está corriendo, no tenemos el plazo— y en todas se leía igual: como un dato
    que falta. Un comerciante que ve "—" en la columna "Se puede deshacer" no sabe si no
    se puede o si la app no sabe.
  */

  // Deshacer un deshacer sería volver a aplicar la actualización original: se hace con una
  // actualización nueva, no con este botón.
  if (corrida.origen === 'undo') {
    return { clase: 'no_aplica', texto: 'No aplica', frase: null };
  }
  if (corrida.estado === 'undone') {
    return { clase: 'no_aplica', texto: 'Ya la deshiciste', frase: 'ya la deshiciste' };
  }
  if (EN_VUELO.has(corrida.estado)) {
    return {
      clase: 'no_aplica',
      texto: 'Cuando termine',
      frase: 'se va a poder deshacer cuando termine',
    };
  }
  if (corrida.totales.actualizadas === 0) {
    return {
      clase: 'no_aplica',
      texto: 'No cambió ningún precio',
      frase: 'no cambió ningún precio, no hay nada que deshacer',
    };
  }
  if (corrida.sePuedeDeshacerHasta === null) {
    return { clase: 'no_aplica', texto: 'No aplica', frase: null };
  }

  if (new Date(corrida.sePuedeDeshacerHasta) <= ahora) {
    return {
      clase: 'vencido',
      texto: `Venció el ${fecha(corrida.sePuedeDeshacerHasta)}`,
      frase: `ya no se puede deshacer, venció el ${fecha(corrida.sePuedeDeshacerHasta)}`,
    };
  }
  return {
    clase: 'disponible',
    texto: `Hasta el ${fecha(corrida.sePuedeDeshacerHasta)}`,
    frase: `se puede deshacer hasta el ${fecha(corrida.sePuedeDeshacerHasta)}`,
  };
}
